import { inventoryStepOver, inventorySizeIdForBaseFt } from './step-over.js';
import { sizeIdOf, itemsCol } from '../../shared/array-types.js';
// lift-occupancy.ts — Janus lane L2 / I1 session 3: the two server reads a Wind Wall lift makes, twinned for the browser.
//
//   liftObstacles()        ft_encounter_occupancy_obstacles($encounter, $object, [$id => true], support_plane_z: true) — what can
//                          stop the lift column (ft_painted_wall_wind_clear_apex), and, with `destination`, the compound-excluded,
//                          wall-binding-aware set ft_resolve_free_position() judges the apex against.
//   liftDestinationClear() ft_resolve_move_destination($encounter, $object, $x, $apexZ, $y, walk: true, free_elevation: true) answering
//                          PLACED at the column without a sideways nudge (ft_painted_wall_wind_settlement_plan's acceptance test).
//
// Pure: the caller resolves each row into facts (creatureFacts() reads a served creature row through the caller's readers). A fact the
// page cannot establish exactly is UNCERTAIN, and an uncertain row hands back ONLY when it could matter — its known footprint (or an
// unbounded one) reaches the column; anything else is answered exactly. PHP: includes/functions/janus/encounter/ft_ground_occupancy.php.
// Oracle: tools/probes/h4_wind_wall_oracle.php (crowd + grid) → tools/test/janus-h4-wind-wall.test.mjs.
import { LATCHED_STATUS_ID } from '../movement/latch-link.js';
import { clearanceSqueezeLevel, placementLegality, wallPenetrationDepth } from '../../../../ft3d/engine/ft3d-move-core.js';
import { squeezeBodyFt } from '../../../../ft3d/engine/ft3d-creature-size.js';
import { floorBandRoomForPosition } from '../../encounter-creation-engine/floor-band.js';
import { withRoofDecks, type RestShape, type ShapeBox, type Zone } from './floor-move.js';
import { boxIsWalledRoom } from '../../../../ft3d/engine/ft3d-shape-core.js';
import { wallOccupancyParts, type PaintedWallGeometry } from '../../shared/painted-wall-geometry.js';
import { parsePaintedWallSectionSnapshot } from '../../shared/painted-wall-section-state.js';
import { paintedWallSection } from '../../shared/painted-wall-sections.js';

type Row = Record<string, unknown>;
const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const FEET_PER_METER = 1 / 0.3048;
/** PHP 8 is_numeric(): an int/float, or a numeric string (leading / trailing whitespace allowed). */
export function phpIsNumeric(v: unknown): boolean {
  if (typeof v === 'number') return Number.isFinite(v);
  return typeof v === 'string' && /^\s*[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?\s*$/.test(v);
}
/** PHP (int) cast: numbers truncate, strings take their leading integer ("20junk" → 20), bools 0/1, else 0. */
export function phpInt(v: unknown): number {
  if (typeof v === 'number') return Number.isFinite(v) ? Math.trunc(v) : 0;
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (typeof v === 'string') { const m = /^\s*([+-]?\d+(\.\d*)?([eE][+-]?\d+)?)/.exec(v); return m ? Math.trunc(Number(m[1])) || 0 : 0; }
  return 0;
}

/** A fact the page cannot establish could change the answer: the caller hands back. */
export class LiftUncertain extends Error {}

export const HIDDEN_STATUS = '47667406-1B72-9D60-310B-D8CF6BFE397D';
/** ft_compound_actor_ids()'s link statuses (riding / ridden / piloting / piloted / grappling / grappled). */
export const COMPOUND_LINK_STATUSES = new Set([LATCHED_STATUS_ID, 'FF8D219A-2A83-0EE5-A222-E748753E8D0B', 'F13F6A58-FE91-F24D-12BE-BE7CCAD81A40',
  'A1F1FAF2-DA1E-D537-36C2-2B74DC82CB12', 'DDC66C31-4093-84AF-18BB-093D924B8738', 'E4A34093-AAAB-6453-BCE0-FBA65EE263AD', '0C2A8037-B0BC-8300-C818-198881B00E7D']);
export const CURTAIN_ITEM_ID = 'E35239DD-DDAD-4C19-9AE6-1EE4FF9C1299';
/** SwarmTemplate (additions.php): monster_qty / width_lanes. */
const SWARM_TEMPLATES: Record<string, { qty: number; lanes: number }> = {
  'C721A8A3-C301-AE2B-B388-D73FE51F65CC': { qty: 5, lanes: 1 }, 'B93C548F-95A2-14A0-129C-C6D40B38C7CB': { qty: 20, lanes: 2 },
  'BD05AC4E-43DC-BC8F-E5A9-D9AA454A9D2B': { qty: 50, lanes: 3 }, '1BE8C9F6-F3E8-2618-7FCC-1956C6C15139': { qty: 200, lanes: 4 },
};

/** SizeType widths (additions.php) — the fallback when the page's size table is not loaded (share it: one resolver everywhere). */
export const SIZE_WIDTHS_FT: Record<string, number> = {
  '13456FEC-A2EE-DB46-5829-0EB2F627E1B7': 2.5, 'AAC1F466-7A8B-C593-1ADC-FC402B8909D7': 3.75, 'DC23AA88-CB3D-EA4E-F6F7-6389ABC4F9DC': 5,
  '52FFDB16-A991-C8D4-FE59-7C75EE6CF7AD': 10, 'D0EF6D7A-82BC-1E6E-9D36-5004C7E0561C': 15, 'CEA2946E-D395-46EA-A6E7-74D21EA2376E': 20,
  'C94CDD74-8ACD-26B0-5C53-4CA29CF21D68': 25, '4C33FD22-5382-15DA-1C46-F57F14378AFD': 30,
};

export interface LiftReaders {
  /** get_size_dimensions(width) — null when the id is unknown (PHP then reads Medium, 5). */
  sizeBase: (id: string) => number | null;
  /** layer_value() on a creature row: the computed bridges, the host slot, then its carriers. */
  layerValue: (row: Row, name: string) => unknown;
}

// ---------------------------------------------------------------------------------------------------------------
// Creature rows
// ---------------------------------------------------------------------------------------------------------------
/** pos_coord(): `position` (post-flip), else legacy `pos_arr`, else a bare container; [0]-wrapped values unwrap; non-numeric = null. */
export function posCoord(row: Row, axis: 'x_pos' | 'y_pos' | 'z_pos'): number | null {
  const pos = asRow(row.position) ?? asRow(row.pos_arr) ?? (['x_pos', 'y_pos', 'z_pos'].some(k => k in row) ? row : null);
  if (!pos || !(axis in pos)) return null;
  let value = pos[axis];
  if (Array.isArray(value)) value = value.length ? value[0] : null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value);
  return null;
}
const ec = (row: Row): Row => asRow(row.encounter_creatures) ?? {};
const field = (row: Row, key: string): unknown => ec(row)[key] ?? row[key];
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
function statusTargets(row: Row): Row[] {
  const list = asRow(row.statuses)?.target;
  return Array.isArray(list) ? list.map(asRow).filter((s): s is Row => !!s) : [];
}
export function statusItemId(status: Row): string {
  return upper(asRow(status.items)?.item_id ?? status.status_item_id ?? asRow(status.status_arr)?.item_id ?? (typeof status.status_arr === 'string' ? status.status_arr : ''));
}

/** Everything ft_encounter_occupancy_obstacles() reads from one creature row. */
export interface CreatureFacts {
  id: string; x: number; y: number; z: number;
  dead: boolean; private: boolean; hidden: boolean; encounterCreature: boolean;
  /** encounter_row_team_uuid(). */
  team: string | null;
  /** Resolved size-tier width; unsized saved bodies use the documented Medium occupancy cube. */
  tierFt: number | null;
  /** ft_creature_physical_mesh_bounds() in feet [x, y(up), z]. */
  meshFt: [number, number, number] | null;
  /** ft_swarm_occupied_width_ft(row, base) as a function of base; null = not a swarm. */
  swarm: ((base: number) => number) | null;
  /** The swarm inputs the page cannot establish exactly (vitality store absent / non-numeric). */
  swarmUncertain: boolean;
  /** encounter_row_is_carried_body(): true / false, or null when the page cannot tell (no carried-id read, and no floor body names it). */
  carried: boolean | null;
  /** Compound link statuses on this row: the caster each names. */
  links: string[];
  /** Other ids the row occupies space under (ft_compound_actor_ids()'s row_ids: an inventory bucket's id on a creature row). */
  aliases?: string[];
}

/** ft_creature_physical_mesh_bounds(): the first valid world GLB/GLTF in rank order (ft3d_avatar, other, ft3d_avatar_clothed), in feet. */
export function creatureMeshFt(row: Row): [number, number, number] | null {
  if (!asRow(row.encounter_creatures)) return null;
  const list = asRow(row.assets)?.['3d'];
  const meshes = (Array.isArray(list) ? list : []).map(asRow).filter((m): m is Row => !!m);
  const rank = (m: Row): number => m.variant === 'ft3d_avatar' ? 0 : m.variant === 'ft3d_avatar_clothed' ? 2 : 1;
  const ordered = meshes.map((m, i) => ({ m, i })).sort((a, b) => rank(a.m) - rank(b.m) || a.i - b.i).map(e => e.m);
  for (const mesh of ordered) {
    if (String(mesh.role ?? 'world') !== 'world' || !['glb', 'gltf'].includes(String(mesh.kind ?? ''))
      || !/^https:\/\/cdn\.fatetide\.com\/assets\/meshes\/[a-f0-9]{64}\.glb$/.test(String(mesh.url ?? ''))
      || !/^[a-f0-9]{64}$/.test(String(mesh.sha256 ?? '')) || !(phpInt(mesh.bytes ?? 0) > 0)) continue;
    const bounds = mesh.boundsM;
    if (!Array.isArray(bounds) || bounds.length !== 3) continue;
    if (!bounds.every(v => phpIsNumeric(v) && Number(v) > 0)) continue;
    return bounds.map(v => Number(v) * FEET_PER_METER) as [number, number, number];
  }
  return null;
}

/** ft_swarm_template_row(): the first creature_template entry (bucket list, then the legacy root slot) that is a SwarmTemplate. */
function swarmTemplate(row: Row): { qty: number; lanes: number } | null {
  for (const raw of [asRow(row.item_layer_types)?.creature_template, row.creature_template]) {
    if (raw === undefined || raw === null) continue;
    const entries = Array.isArray(raw) ? raw : [raw];
    for (const entry of entries) {
      const e = asRow(entry);
      const id = typeof entry === 'string' ? entry : String(e?.item_layer_effect ?? e?.item_id ?? asRow(e?.items)?.item_id ?? '');
      const hit = SWARM_TEMPLATES[upper(id.split('|')[0])];
      if (hit) return hit;
    }
  }
  return null;
}

/**
 * `carriedIds` = ft_corpse_carried_creature_ids() for this encounter (served beside the lock bindings) — with it every dead row is
 * answered exactly; without it a dead row no floor body names stays uncertain.
 */
export function creatureFacts(row: Row, read: LiftReaders, floorBodyCreatureIds: ReadonlySet<string>, carriedIds?: ReadonlySet<string> | null): CreatureFacts {
  const id = upper(field(row, 'encounter_creature_id'));
  const dead = Number(scalar(field(row, 'encounter_creature_dead')) ?? 0) === 1;
  const sizeId = (() => {
    const resolved = sizeIdOf(row);
    if (resolved) return resolved;
    const v = read.layerValue(row, 'size');
    if (v !== null && v !== undefined && v !== '') return String(v);
    const e = asRow(row.encounter_creatures);
    const w = e ? read.layerValue(e, 'size') : null;
    return w !== null && w !== undefined && w !== '' ? String(w) : null;
  })();
  const width = sizeId ? read.sizeBase(sizeId) ?? SIZE_WIDTHS_FT[upper(sizeId)] ?? null : null;
  // ft_ground_occupancy_base_size: an unreadable tier occupies a 5ft cube. This is a geometry
  // default, never a replacement for an ancestry size or a prerequisite to saving an avatar.
  if (width === null || width <= 0) console.info('janus-placement-size-default: Medium (5 ft)',
    itemsCol(row, 'item_name') ?? asRow(row.player_characters)?.player_name ?? field(row, 'encounter_creature_name') ?? 'Creature');
  const tpl = swarmTemplate(row);
  let swarm: CreatureFacts['swarm'] = null, swarmUncertain = false;
  if (tpl) {
    const bucket = ec(row);
    const qty = phpInt(bucket.swarm_members_start ?? tpl.qty);
    const alive = bucket.swarm_members !== undefined && bucket.swarm_members !== null
      ? Math.max(0, Math.min(1, phpInt(bucket.swarm_members) / qty)) : 1;
    const lanesRaw = read.layerValue(row, 'swarm_width_lanes');
    const lanes = phpIsNumeric(lanesRaw) ? Math.max(0, phpInt(lanesRaw)) : tpl.lanes;
    const store = asRow(asRow(row.computed)?.vitality);
    const cur = store?.v_current, max = store?.effective_v_max;
    // computed_value() may derive a missing key the raw store does not carry — only a store holding both is certain.
    swarmUncertain = !store || cur === undefined || max === undefined;
    let ratio = 1;
    if (phpIsNumeric(cur) && phpIsNumeric(max) && Number(max) > 0) {
      ratio = Math.min(1, Math.max(0, Number(cur) / Number(max)));
    }
    ratio *= alive;
    swarm = (base: number): number => {
      if (base <= 0 || qty < 2 || lanes <= 0) return base;
      const q = ratio <= 0 ? 0 : Math.max(1, Math.ceil(qty * ratio - 1e-9));
      const extra = q <= 1 ? 0 : Math.ceil(lanes * q / qty);
      return base + extra * Math.min(base, 5);
    };
  }
  const statuses = statusTargets(row);
  const teamRaw = asRow(field(row, 'team')) ? asRow(asRow(field(row, 'team'))!.items)?.item_id : scalar(field(row, 'team'));
  const team = teamRaw !== null && teamRaw !== undefined && teamRaw !== '' && typeof teamRaw !== 'object' ? String(teamRaw) : ((): string | null => {
    const v = read.layerValue(row, 'team'); return v === null || v === undefined || v === '' || typeof v === 'object' ? null : String(v);
  })();
  return {
    id, x: posCoord(row, 'x_pos') ?? 0, y: posCoord(row, 'y_pos') ?? 0, z: posCoord(row, 'z_pos') ?? 0,
    dead, private: Number(scalar(field(row, 'encounter_creature_private')) ?? 0) === 1,
    hidden: statuses.some(s => statusItemId(s) === HIDDEN_STATUS), encounterCreature: id !== '',
    team, tierFt: width !== null && width > 0 ? width : 5,
    meshFt: creatureMeshFt(row), swarm, swarmUncertain,
    carried: !dead ? false : carriedIds ? id !== '' && carriedIds.has(id) : floorBodyCreatureIds.has(id) ? false : null,
    links: statuses.filter(s => COMPOUND_LINK_STATUSES.has(statusItemId(s))).map(s => upper(s.status_caster_id)).filter(Boolean),
    aliases: ((): string[] => { let v = asRow(row.inventory)?.inventory_id; if (Array.isArray(v)) v = v[0]; const a = upper(v); return a && a !== id ? [a] : []; })(),
  };
}

/** The fixed mesh body or size-tier cylinder, widened along +x for swarms. Unsized bodies occupy the documented 5ft cube. */
export function creatureZone(c: CreatureFacts): Zone {
  const base = c.meshFt ? Math.max(c.meshFt[0], c.meshFt[2]) : c.tierFt ?? 5;
  const height = c.meshFt ? c.meshFt[1] : base;
  const width = c.swarm ? c.swarm(base) : base;
  if (width > base + 1e-6) return { kind: 'oblong', x: c.x, y: c.y, halfW: width / 2, halfD: base / 2, facing: 0, zMin: c.z, zMax: c.z + height };
  return { kind: 'circle', x: c.x, y: c.y, r: base / 2, zMin: c.z, zMax: c.z + height };
}

/** ft3d_corpse_support_height_ft() (null for the living and the carried). */
export function corpseHeightFt(c: CreatureFacts, carried: boolean): number | null {
  if (!c.dead || carried) return null;
  if (c.meshFt) return c.meshFt[2] + 0.025 / 0.3048;
  const tier = c.tierFt ?? 5;
  return Math.max(0.15 / 0.3048, c.swarm ? c.swarm(tier) : tier);
}

// ---------------------------------------------------------------------------------------------------------------
// Inventory rows
// ---------------------------------------------------------------------------------------------------------------
export interface LiftInventoryFacts {
  id: string; itemId: string; itemType: string;
  /** ft_inventory_is_solid_obstacle(). */
  solid: boolean;
  /** ft_ground_occupancy_shape(row, support_plane_z: true). */
  zone: Zone;
  z: number;
  /** ft_ground_occupancy_base_size(row) (size tier) and the transform's uniform scale — the tree trunk's inputs. */
  tierFt: number; scale: number;
  /** Resolved once with the same scaled geometry as zone; absent legacy facts are Medium. */
  stepOverSizeId?: string;
  /** A terrain_type layer the page sees on the row (collect_difficult_terrain_inventory_ids may list it). */
  terrainTyped: boolean;
  /** Compound link statuses on this row: the caster each names. */
  links?: string[];
}

/** ft_inventory_is_bound_curtain()'s bindings: curtain rows an authored opening covers (ft_curtain_binding), from the RAW shape. */
export function boundCurtainIds(rawShape: unknown): Set<string> | undefined {
  let shape = rawShape;
  if (typeof shape === 'string') { try { shape = JSON.parse(shape); } catch { return undefined; } }
  if (!asRow(shape)) return undefined;
  const out = new Set<string>();
  for (const opening of (Array.isArray(asRow(shape)?.openings) ? asRow(shape)!.openings as unknown[] : []).map(asRow)) {
    const creation = asRow(opening?.creation), binding = asRow(creation?.covering), rooms = creation?.rooms;
    if (!binding || binding.role !== 'curtain' || typeof binding.inventoryId !== 'string' || !/^[A-F0-9]{8}(?:-[A-F0-9]{4}){3}-[A-F0-9]{12}$/i.test(binding.inventoryId)
      || typeof binding.roomId !== 'string' || !Array.isArray(rooms) || !rooms.includes(binding.roomId)) continue;
    out.add(upper(binding.inventoryId));
  }
  return out;
}

/** ft_inventory_is_bound_door()'s bindings (MV5 2026-10-02): door rows an authored opening is bound to
 *  (`creation.connectionIntent.doorInventoryId`), from the RAW shape — not standing obstacles; the opening governs the doorway.
 *  MV2 (Codex round 11 C4): read exactly as PHP and preview3d.ts read it — `openings` a LIST, an opening an object, the id a STRING,
 *  trimmed + upper-cased (a blank id binds nothing). */
export function boundDoorIds(rawShape: unknown): Set<string> | undefined {
  let shape = rawShape;
  if (typeof shape === 'string') { try { shape = JSON.parse(shape); } catch { return undefined; } }
  if (!asRow(shape)) return undefined;
  const out = new Set<string>();
  for (const opening of (Array.isArray(asRow(shape)?.openings) ? asRow(shape)!.openings as unknown[] : []).map(asRow)) {
    const bound = asRow(asRow(opening?.creation)?.connectionIntent)?.doorInventoryId;
    if (typeof bound === 'string' && upper(bound) !== '') out.add(upper(bound));
  }
  return out;
}

/** ft_ground_tree_trunk_zone(row, support_plane_z: true): the trunk circle a stored `tree` collides as. */
export function treeTrunkZone(r: LiftInventoryFacts): Zone | null {
  if (r.itemType !== 'tree') return null;
  const scale = Number.isFinite(r.scale) && r.scale > 0 ? r.scale : 1;
  const diameter = Math.min(4, Math.max(1, r.tierFt * scale * 0.16));
  return { kind: 'circle', x: r.zone.x, y: r.zone.y, r: diameter / 2, zMin: r.z, zMax: r.z + r.tierFt };
}

/** The standing footprint; thin dimensioned floor furnishings are stepped over. */
export function inventoryStandingZone(row: LiftInventoryFacts): Zone | null {
  const zone = treeTrunkZone(row) ?? row.zone;
  return zone.kind === 'oblong' && zone.zMax - zone.zMin <= 0.1 ? null : zone;
}

// ---------------------------------------------------------------------------------------------------------------
// The obstacle set
// ---------------------------------------------------------------------------------------------------------------
export interface LiftWorld {
  creatures: CreatureFacts[];
  rows: LiftInventoryFacts[];
  /** collect_difficult_terrain_inventory_ids(): the stamped difficult-terrain footprints' inventory ids. */
  difficultIds: ReadonlySet<string>;
  /** ft_painted_wall_inventory_geometry_index(collect_persistent_aoes()): a bound row collides as its wall section at the apex. */
  walls: WallBindingIndex;
  /** Curtain rows bound to a shape opening (ft_inventory_is_bound_curtain); undefined = the page cannot tell. */
  boundCurtainIds?: ReadonlySet<string>;
  /** Door rows bound to a shape opening (ft_inventory_is_bound_door, MV5); undefined = every door stays an obstacle (stricter). */
  boundDoorIds?: ReadonlySet<string>;
}
export interface LiftMover {
  id: string;
  /** ft_ground_occupancy_base_size($object): its size tier (the corpse step-over rule). */
  tierFt: number;
  /** encounter_row_team_uuid($object). */
  team: string | null;
  /** Unused: the object's own link statuses reach the compound graph through its roster row (as in PHP). */
  links?: string[];
}

/** Shared standing-obstacle membership; sweep pass-through flags do not govern a landing. Null = curtain bindings not held. */
export function inventoryOccupancyIncluded(world: Pick<LiftWorld, 'difficultIds' | 'boundCurtainIds' | 'boundDoorIds'>,
  row: Pick<LiftInventoryFacts, 'id' | 'itemId' | 'itemType' | 'solid' | 'stepOverSizeId'>,
  mover?: Pick<LiftMover, 'tierFt'>): boolean | null {
  if (!row.solid) return false;
  if (row.itemId === CURTAIN_ITEM_ID) {
    if (!world.boundCurtainIds) return null;
    if (world.boundCurtainIds.has(row.id)) return false;
  }
  if (row.itemType === 'door' && world.boundDoorIds?.has(row.id)) return false;
  if (mover && inventoryStepOver(inventorySizeIdForBaseFt(mover.tierFt), row.stepOverSizeId)) return false;
  return !world.difficultIds.has(row.id);
}

const UUID_RE = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
/**
 * ft_compound_actor_ids(): the undirected link graph over every creature and inventory row (edges = a row's link statuses → the caster
 * each names), walked breadth-first from the object. The object's own statuses count through its roster row, exactly as PHP reads them.
 */
export function compoundActorIds(world: Pick<LiftWorld, 'creatures' | 'rows'>, moverId: string): Set<string> {
  const adjacency = new Map<string, Set<string>>();
  const link = (a: string, b: string): void => {
    if (a === '' || b === '' || a === b) return;
    if (!adjacency.has(a)) adjacency.set(a, new Set());
    if (!adjacency.has(b)) adjacency.set(b, new Set());
    adjacency.get(a)!.add(b); adjacency.get(b)!.add(a);
  };
  // A row's own ids are the same occupant (they travel together); each link status joins every one of them to its caster.
  for (const node of [...world.creatures.map(c => ({ ids: [c.id, ...(c.aliases ?? [])], links: c.links })), ...world.rows.map(r => ({ ids: [r.id], links: r.links ?? [] }))]) {
    const ids = node.ids.filter(v => v !== '');
    for (const a of ids) for (const b of ids) link(a, b);
    for (const partner of node.links) if (UUID_RE.test(partner)) for (const self of ids) link(self, partner);
  }
  const ids = new Set([moverId]);
  const queue = [moverId];
  while (queue.length) {
    for (const next of adjacency.get(queue.shift()!) ?? []) if (!ids.has(next)) { ids.add(next); queue.push(next); }
  }
  return ids;
}

/** A painted-wall binding index: solid / frozen section rows → their section geometry. */
export interface WallBindingIndex {
  bound: ReadonlyMap<string, PaintedWallGeometry>;
  /** Rows a source lists whose binding the page cannot establish (a zone-anchor key not served at the top level, or a JSON float spelling
   * of an integer PHP's is_int() rejects): every geometry the row might collide as. */
  uncertain: ReadonlyMap<string, readonly PaintedWallGeometry[]>;
  /** PHP throws — or may (an uncertain source shares a row with another source, or lacks its identity): the apex resolver hands back. */
  conflict: boolean;
  /** Conservative source extents for every conflict; absent on an older index means unbounded. */
  conflictGeometry?: readonly PaintedWallGeometry[];
}
/** A JSON integer written as a float ("index": 0.0) — JSON.parse cannot tell it from 0, PHP's is_int() rejects it. */
const FLOAT_INTEGER = /"(?:castLevel|revision|index)"\s*:\s*-?\d+(?:\.\d*|[eE][+-]?\d+)/;
/**
 * ft_painted_wall_inventory_geometry_index() over collect_persistent_aoes()'s status rows (the caller filters paused ones and walks the
 * encounter / inventory / self-aura lists). PHP reads every source field at the TOP level. Retired phases stay in the ownership check but
 * never collide; the ownership fingerprint is the whole binding (anchor, ability, index, phase, extent, geometry).
 */
export function wallBindingIndex(sources: readonly Row[]): WallBindingIndex {
  const bound = new Map<string, PaintedWallGeometry>(), uncertain = new Map<string, PaintedWallGeometry[]>(), owners = new Map<string, string>();
  const listed = new Map<string, PaintedWallGeometry[]>(), uncertainIds = new Set<string>();
  const conflictGeometry: PaintedWallGeometry[] = [];
  let conflict = false;
  for (const st of sources) {
    const f = (k: string): unknown => { const v = st[k]; return Array.isArray(v) ? v[0] : v; };
    // persistent_aoe_status_is_zone_anchor(): status_aoe_source = 1 and an AOE distance (radius or distance point).
    if (phpInt(f('status_aoe_source') ?? 0) !== 1) continue;
    // ft_painted_wall_section_snapshot(): ≤ 150 000 chars, valid geometry, castLevel a non-negative int, a valid sections state.
    const raw = f('status_aoe_geometry') ?? null;
    let parsed: unknown = raw;
    if (typeof raw === 'string') { if (raw.length > 150000) continue; try { parsed = JSON.parse(raw); } catch { continue; } }
    const top = asRow(parsed);
    if (top && Object.prototype.hasOwnProperty.call(top, 'castLevel') && !(Number.isSafeInteger(top.castLevel) && (top.castLevel as number) >= 0)) continue;
    const snapshot = parsePaintedWallSectionSnapshot(parsed);
    if (!snapshot || !snapshot.sections) continue;
    const typeUnknown = typeof raw === 'string' && FLOAT_INTEGER.test(raw);
    const present = (k: string): boolean => { const v = f(k); return v !== null && v !== undefined; };
    const anchorKnown = !typeUnknown && (present('status_aoe_distance') || present('status_distance_point_x'));
    const anchor = upper(f('status_id'));
    // PHP throws on a missing anchor identity before reading any entry.
    if (anchor === '') { conflict = true; conflictGeometry.push(snapshot.geometry); continue; }
    const ability = upper(f('status_origin_id') ?? f('status_item_id') ?? '');
    for (const entry of snapshot.sections.entries) {
      const id = entry.inventoryId;
      const section = paintedWallSection(snapshot.geometry, entry.index, snapshot.sections.sectionFt);
      if (!section) { conflict = true; conflictGeometry.push(snapshot.geometry); continue; }
      listed.set(id, [...(listed.get(id) ?? []), section.geometry]);
      if (!anchorKnown) { uncertainIds.add(id); uncertain.set(id, [...(uncertain.get(id) ?? []), section.geometry]); continue; }
      const value = JSON.stringify([anchor, ability, entry.index, entry.phase, section.startFt, section.endFt, section.geometry]);
      if (owners.has(id) && owners.get(id) !== value) { conflict = true; conflictGeometry.push(...listed.get(id)!); }
      owners.set(id, value);
      if (entry.phase === 'solid' || entry.phase === 'frozen') bound.set(id, section.geometry);
    }
  }
  // A row an uncertain source lists beside any other listing: PHP may see two owners and throw.
  for (const id of uncertainIds) if ((listed.get(id)?.length ?? 0) > 1) { conflict = true; conflictGeometry.push(...listed.get(id)!); }
  return { bound, uncertain, conflict, conflictGeometry };
}

/** Horizontal reach of the probe footprint vs a zone (a conservative superset: bounding circles). */
function mayTouch(a: Zone, b: Zone): boolean {
  const ra = a.kind === 'circle' ? a.r : Math.hypot(a.halfW, a.halfD);
  const rb = b.kind === 'circle' ? b.r : Math.hypot(b.halfW, b.halfD);
  return Math.hypot(a.x - b.x, a.y - b.y) < ra + rb + 1e-6;
}

/**
 * ft_encounter_occupancy_obstacles() for the lifted object. `probe` is the object's footprint at the column: an uncertain row hands back
 * only when it could reach it. `destination` = the apex resolver's variant (compound members excluded, wall-bound rows collide as walls).
 * `standingTopPaddingFt` prevents top contact with ordinary props without changing creature or painted-wall zones.
 */
export function liftObstacles(world: LiftWorld, mover: LiftMover, probe: Zone, destination = false, standingTopPaddingFt = 0): Zone[] {
  const out: Zone[] = [];
  const step = Math.max(2.5, mover.tierFt);
  const propMover = world.creatures.some(c => c.id === mover.id) ? mover : undefined;
  // The lift column excludes the object alone; the apex resolver excludes its whole compound group (ft_resolve_free_position).
  const exclude = destination ? compoundActorIds(world, mover.id) : new Set([mover.id]);
  // The destination variant builds the wall index first; PHP throws on conflicting ownership before any row is read.
  if (destination && world.walls.conflict) throw new LiftUncertain('lift-wall-section');
  for (const c of world.creatures) {
    if (exclude.has(c.id)) continue;
    const zone = creatureZone(c);
    const uncertain = (reason: string): void => { if (mayTouch(probe, zone)) throw new LiftUncertain(reason); };
    if (c.carried === true) continue;
    // A body as low as the object's step is stepped over whether carried or lying there.
    const lying = corpseHeightFt(c, false);
    if (lying !== null && lying <= step) continue;
    if (c.private) continue;
    if (c.encounterCreature && c.hidden && mover.team !== null) { uncertain('lift-hidden-team'); continue; }
    if (c.swarm && c.swarmUncertain) { uncertain('lift-swarm-vitality'); continue; }
    if (c.carried === null) { uncertain('lift-body-carried'); continue; }
    const z = { ...zone };
    if (lying !== null) z.zMax = z.zMin + lying;
    out.push(z);
  }
  for (const r of world.rows) {
    if (exclude.has(r.id)) continue;
    const included = inventoryOccupancyIncluded(world, r, world.walls.bound.has(r.id) || world.walls.uncertain.has(r.id) ? undefined : propMover);
    if (included === false) continue;
    const trunk = treeTrunkZone(r);
    const uncertain = (reason: string): void => { if (mayTouch(probe, r.zone) || (trunk !== null && mayTouch(probe, trunk))) throw new LiftUncertain(reason); };
    if (included === null) { uncertain('lift-curtain'); continue; }
    if (r.terrainTyped) { uncertain('lift-terrain-row'); continue; }
    if (destination) {
      // ft_painted_wall_occupancy_parts(): a bound section collides as its own capsule for THIS body's z span (wall_mover_span = the probe).
      const wall = world.walls.bound.get(r.id);
      if (wall) { out.push(...wallOccupancyParts(wall, probe.zMin, probe.zMax) as Zone[]); continue; }
      const maybe = world.walls.uncertain.get(r.id);
      if (maybe && [r.zone, ...maybe.flatMap(g => wallOccupancyParts(g, probe.zMin, probe.zMax) as Zone[])].some(z => mayTouch(probe, z))) throw new LiftUncertain('lift-wall-section');
    }
    const zone = inventoryStandingZone(r);
    if (!zone) continue;
    out.push(standingTopPaddingFt ? { ...zone, zMax: zone.zMax + standingTopPaddingFt } : zone);
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// The apex destination
// ---------------------------------------------------------------------------------------------------------------
/** ft_ground_floor_band_shape_for_position(): the rooms on the band under the feet + the openings that belong to them. */
export function floorBandShape(shape: RestShape, x: number, y: number, z: number, canvasExterior = false): { shape: RestShape; floor: number } | null {
  const decked = withRoofDecks(shape, z);
  if (!decked.boxes.length) return { shape: decked, floor: 0 };
  const floor = (floorBandRoomForPosition(decked.boxes, x, y, z) as ShapeBox | null)?.floor ?? 0;
  const boxes = decked.boxes.filter(box => Number.isFinite(Number(box.floor)) && Math.abs(Number(box.floor) - floor) <= 0.01);
  // No authored room on ground band 0 is still a valid band. The caller decides
  // whether its canvas supplies exterior support; never fall back to other floors.
  if (!boxes.length && !canvasExterior) return null;
  const roomIds = new Set(boxes.map(b => String(b.id ?? '')));
  const openings = (Array.isArray(decked.openings) ? decked.openings : []).filter(raw => {
    const o = asRow(raw);
    if (!o) return false;
    const rooms = asRow(o.creation)?.rooms;
    if (Array.isArray(rooms) && rooms.length) return rooms.every(id => roomIds.has(String(id)));
    const plane = asRow(o.plane), span = asRow(o.span), oz = asRow(o.z);
    const axis = String(plane?.axis ?? '');
    if (axis !== 'x' && axis !== 'y') return false;
    const at = Number(plane?.at ?? Infinity), sMin = Number(span?.min ?? Infinity), sMax = Number(span?.max ?? -Infinity);
    const zMin = Number(oz?.min ?? Infinity), zMax = Number(oz?.max ?? -Infinity);
    if (![at, sMin, sMax, zMin, zMax].every(Number.isFinite) || zMax <= zMin) return false;
    return boxes.some(box => {
      const face = box[axis], other = box[axis === 'x' ? 'y' : 'x'];
      return (Math.abs(at - face.min) <= 1e-6 || Math.abs(at - face.max) <= 1e-6) && sMin >= other.min - 1e-6 && sMax <= other.max + 1e-6
        && sMax > sMin && zMax > box.floor + 1e-6 && zMin < box.ceiling - 1e-6;
    });
  });
  return { shape: { ...decked, boxes, openings }, floor };
}

function hasExteriorOpen(shape: RestShape): boolean {
  return shape.boxes.some(box => { const c = asRow(box.creation) ?? {}; return c.enclosure === 'none' || c.environment === 'exterior-open' || c.presetId === 'exterior-open'; });
}
/** ft_ground_with_exterior_open_ground(): bounded query-only open terrain on a band that authors an exterior (or stands on an open canvas). */
export function withExteriorOpenGround(shape: RestShape, fromX: number, fromY: number, toX: number, toY: number, margin: number, canvasExterior: boolean): RestShape {
  let xMin = Math.min(fromX, toX), xMax = Math.max(fromX, toX), yMin = Math.min(fromY, toY), yMax = Math.max(fromY, toY);
  let floor = shape.boxes.length ? Infinity : 0, ceiling = -Infinity;
  for (const box of shape.boxes) {
    xMin = Math.min(xMin, box.x.min); xMax = Math.max(xMax, box.x.max); yMin = Math.min(yMin, box.y.min); yMax = Math.max(yMax, box.y.max);
    floor = Math.min(floor, box.floor); ceiling = Math.max(ceiling, box.ceiling);
  }
  const canvasGround = canvasExterior && Math.abs(floor) < 1e-6;
  if (shape.boxes.length && !canvasGround && !hasExteriorOpen(shape)) return shape;
  return { ...shape, boxes: [...shape.boxes, { id: 'navigation-open-ground', x: { min: xMin - margin, max: xMax + margin }, y: { min: yMin - margin, max: yMax + margin },
    floor, ceiling: Math.max(ceiling, floor + 1000), navigationOpenGround: true }] };
}

export interface LiftDestination {
  /** ft_encounter_shape() — overlaid as the scene renders it. */
  shape: RestShape;
  /** ft_encounter_canvas_is_exterior(): encounter_inside !== 1. */
  canvasExterior: boolean;
  /** The object's persisted feet (the free-elevation floor anchor). */
  persisted: { x: number; y: number; z: number };
  /** ft_ground_occupancy_shape($object, x, y, z, support_plane_z: true). */
  footprintAt: (x: number, y: number, z: number) => Zone;
  /** ft_ground_occupancy_height_ft($object). */
  heightFt: number;
  /** ft_ground_occupancy_base_size($object). */
  tierFt: number;
  /** The obstacles for the apex (liftObstacles(..., destination: true) against the mover there). */
  obstacles: (mover: Zone) => Zone[];
  /** The page's shape may lack live opening / scene-wall overlays: a clearance answer that reads one hands back. */
  overlayUnknown?: boolean;
  /** Creature movement uses the SizeType ladder; inventory retains its rigid footprint. */
  allowSqueeze?: boolean;
  squeezeSizeFt?: number;
}

/** Shared floor-band and overlay proof for destination clearance and wall penetration. */
function liftDestinationProbe(d: LiftDestination, x: number, y: number, apexZ: number) {
  let proposedZ = apexZ;
  let movement: RestShape = d.shape;
  const origin = floorBandShape(d.shape, d.persisted.x, d.persisted.y, d.persisted.z, d.canvasExterior);
  if (origin) proposedZ = Math.max(proposedZ, origin.floor);
  const band = floorBandShape(d.shape, x, y, proposedZ, d.canvasExterior);
  if (band) movement = withExteriorOpenGround(band.shape, x, y, x, y, 40 + d.tierFt, d.canvasExterior);
  if (Math.abs(proposedZ - apexZ) > 0.001) return null;
  const mover = d.footprintAt(x, y, proposedZ);
  const rayZ = (mover.zMin + mover.zMax) / 2;
  if (d.overlayUnknown) {
    // Unknown openings matter only within reach of a finite structural face; open terrain has no walls.
    const reach = (mover.kind === 'circle' ? mover.r : Math.hypot(mover.halfW, mover.halfD)) + 0.5;
    const near = (axis: 'x' | 'y', at: number, min: number, max: number, half = 0): boolean => {
      const normal = axis === 'x' ? x : y, along = axis === 'x' ? y : x;
      return Math.hypot(Math.max(0, Math.abs(normal - at) - half), Math.max(0, min - along, along - max)) < reach;
    };
    const nearFace = movement.boxes.some(box => boxIsWalledRoom(box) && rayZ >= box.floor - 0.5 && rayZ <= box.ceiling + 0.5
      && ([box.x.min, box.x.max].some(at => near('x', at, box.y.min, box.y.max))
        || [box.y.min, box.y.max].some(at => near('y', at, box.x.min, box.x.max))));
    const nearWall = (movement.sceneWalls ?? []).some(raw => {
      const wall = asRow(raw), span = asRow(wall?.span), band = asRow(wall?.z);
      if (!wall || !span || !band || (wall.axis !== 'x' && wall.axis !== 'y')) return true;
      const at = Number(wall.at), min = Number(span.min), max = Number(span.max), bottom = Number(band.min), top = Number(band.max);
      const half = Math.max(0.05, Number(wall.thicknessFt ?? 0.5)) / 2;
      if (![at, min, max, bottom, top, half].every(Number.isFinite)) return true;
      return rayZ >= bottom - 0.5 && rayZ <= top + 0.5 && near(wall.axis, at, min, max, half);
    });
    if (nearFace || nearWall) throw new LiftUncertain('lift-opening-overlay');
  }
  return { movement, mover, rayZ };
}

/** ft_resolve_move_destination(x, apexZ, y, walk, free elevation) → PLACED at (x, y, apexZ) with no nudge. */
export function liftDestinationClear(d: LiftDestination, x: number, y: number, apexZ: number): boolean {
  const probe = liftDestinationProbe(d, x, y, apexZ);
  return probe !== null && placementLegality(probe.movement as never, probe.mover as never, d.obstacles(probe.mover) as never,
    { rayZ: probe.rayZ, headroomFt: d.heightFt, allowSqueeze: d.allowSqueeze, squeezeSizeFt: d.squeezeSizeFt }) === null;
}

/** Endpoint status uses the exact passage probe, including its floor band and mid-body ray height. */
export function liftDestinationSqueezeLevel(d: LiftDestination, x: number, y: number, z: number): 0 | 1 | 2 | null {
  const probe = liftDestinationProbe(d, x, y, z);
  if (!probe) return null;
  const radius = probe.mover.kind === 'circle' ? probe.mover.r : Math.max(probe.mover.halfW, probe.mover.halfD);
  return clearanceSqueezeLevel(probe.movement as never, x, y, radius, probe.rayZ, d.squeezeSizeFt);
}

/** Null means the obstruction is not a measured wall overlap (union, support, headroom or occupancy). */
export function liftDestinationWallDepth(d: LiftDestination, x: number, y: number, apexZ: number): number | null {
  const probe = liftDestinationProbe(d, x, y, apexZ);
  if (!probe) return null;
  const { movement, mover, rayZ } = probe;
  const reason = placementLegality(movement as never, mover as never, d.obstacles(mover) as never, { rayZ, headroomFt: d.heightFt, allowSqueeze: d.allowSqueeze, squeezeSizeFt: d.squeezeSizeFt });
  if (reason === null) return 0;
  return reason === 'no_clearance' ? wallPenetrationDepth(movement as never, x, y,
    d.allowSqueeze ? squeezeBodyFt(d.squeezeSizeFt ?? d.tierFt, 2) / 2
      : mover.kind === 'circle' ? mover.r : Math.max(mover.halfW, mover.halfD), rayZ, d.persisted) : null;
}
